import React from 'react';
import { Reorder } from 'framer-motion';
import { ArrowDownIcon, GripVerticalIcon, WorkflowIcon } from 'lucide-react';
import type { BuilderController } from './useBuilder';
import { NODE_KINDS } from '../nodeKinds';
import { cn } from '../../../src/lib/utils';
export function BuilderCanvas({
  builder
}: {
  builder: BuilderController;
}) {
  const {
    nodes,
    selectedNode
  } = builder;
  return <div className="flex h-full min-h-0 flex-col" data-id="element-129" data-mp="3s2z6rdark211">
      <div className="flex items-center justify-between gap-2 border-b border-night-line px-4 py-3" data-id="element-130" data-mp="3s3igw7u69m4i">
        <div className="flex items-center gap-2" data-id="element-131" data-mp="3n5z133htt1ag">
          <WorkflowIcon className="h-4 w-4 text-violet-ink" aria-hidden="true" data-id="element-132" data-mp="5o65w2zycxtm" />
          <p className="text-sm font-semibold text-night-text" data-id="element-133" data-mp="wxresjrmm9l">Workflow canvas</p>
        </div>
        <span className="text-[11px] text-night-faint" data-id="element-134" data-mp="39cr93apu23s8">{nodes.length} steps · drag or use Alt + ↑/↓</span>
      </div>

      <Reorder.Group axis="y" values={nodes} onReorder={builder.setNodes} layoutScroll className="dgpt-night-grid min-h-0 flex-1 overflow-y-auto p-4" aria-label="Workflow steps" data-id="element-135" data-mp="34e6b7hy1zw6y">
        {nodes.length === 0 && !builder.building ? <li className="flex h-full min-h-[240px] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-night-line p-6 text-center" data-id="element-136" data-mp="2ppl9cghk7qf8">
            <p className="text-sm font-semibold text-night-text" data-id="element-137" data-mp="2unmtz5umvxst">Empty canvas</p>
            <p className="max-w-xs text-[12px] text-night-muted" data-id="element-138" data-mp="1xzq75j8ls695">Describe a process, start from a DGPT template, or add agents from the Agent Library below.</p>
          </li> : null}
        {nodes.map((node, index) => {
        const kind = NODE_KINDS[node.kind];
        const Icon = kind.icon;
        const selected = selectedNode?.id === node.id;
        const yes = node.settings.find(s => s.key === 'If true')?.value;
        const no = node.settings.find(s => s.key === 'Otherwise')?.value;
        return <Reorder.Item key={node.id} value={node} className="list-none" initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} data-id="element-139" data-mp="2xoxzum1p6y1r" data-mpi={index}>
              <div role="button" tabIndex={0} aria-pressed={selected} aria-label={`${index + 1}. ${node.label}, ${kind.label}. Press Enter to configure.`} onClick={() => builder.selectNode(node.id)} onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              builder.selectNode(node.id);
            }
            if (event.altKey && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
              event.preventDefault();
              builder.moveNode(node.id, event.key === 'ArrowUp' ? -1 : 1);
            }
          }} className={cn('group flex cursor-grab items-center gap-3 rounded-xl border bg-night-panel px-3 py-2.5 transition-colors active:cursor-grabbing focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet', selected ? 'border-violet shadow-[0_0_0_3px_rgba(124,92,255,0.18)]' : 'border-night-line hover:border-violet/50')} data-id="element-140" data-mp="2sotheqh8bt8w">
                <GripVerticalIcon className="h-4 w-4 shrink-0 text-night-faint" aria-hidden="true" data-id="element-141" data-mp="1rgwlhk49lg5" />
                <span className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', kind.tone)} data-id="element-142" data-mp="pgt66ijcvviz">
                  <Icon className="h-4 w-4" aria-hidden="true" data-id="element-143" data-mp="ugnxgn71mn4k" />
                </span>
                <span className="min-w-0 flex-1" data-id="element-144" data-mp="gh64dvmpsnpe">
                  <span className="block truncate text-[13px] font-semibold text-night-text" data-id="element-145" data-mp="ggd6zl9nbajx">{node.label}</span>
                  <span className="block truncate text-[11px] text-night-muted" data-id="element-146" data-mp="kkb11hk45v64">{node.detail}</span>
                </span>
                <span className="hidden shrink-0 rounded-md border border-night-line px-1.5 py-0.5 text-[10px] uppercase tracking-[0.06em] text-night-faint sm:inline" data-id="element-147" data-mp="3587aeilxx2se">
                  {kind.label}
                </span>
              </div>
              {node.kind === 'condition' && (yes || no) ? <div className="ml-11 mt-1.5 flex flex-wrap gap-1.5 text-[10px]" data-id="element-148" data-mp="3bpiqrdehdgco">
                  {yes ? <span className="rounded-md bg-[#3A2E14] px-1.5 py-0.5 text-[#F5C76B]" data-id="element-149" data-mp="36qpgruyek7h9">Yes → {yes}</span> : null}
                  {no ? <span className="rounded-md bg-night-raised px-1.5 py-0.5 text-night-muted" data-id="element-150" data-mp="89ldgvl0vsa4">No → {no}</span> : null}
                </div> : null}
              {index < nodes.length - 1 ? <span className="flex h-6 items-center justify-center text-night-faint" aria-hidden="true" data-id="element-151" data-mp="3ufidhtmumjcx">
                  <ArrowDownIcon className="h-3 w-3" data-id="element-152" data-mp="3qdn4e2yof1ol" />
                </span> : null}
            </Reorder.Item>;
      })}
        {builder.building ? <li className="mt-2 h-12 animate-pulse list-none rounded-xl border border-dashed border-violet/40 motion-reduce:animate-none" aria-hidden="true" data-id="element-153" data-mp="2ezrnaoo7oiuq" /> : null}
      </Reorder.Group>
    </div>;
}